import { all, call, fork, getContext, put, select, take, takeEvery } from 'redux-saga/effects';
import _merge from 'lodash/merge';

import dynaForms from '@elab/core/utils/dynaForms';
import { initAddressRelationsWatchers } from '@elab/core/utils/addressRelations/saga';
import dynaFormsApi from '@elab/core/utils/dynaForms/api';
import { cleanPhoneNumber } from '@elab/core/utils/dynaForms/helpers';
import {
    actions as userActions,
    selectors as userSelectors,
    types as userTypes,
} from '@elab/core/store/user';
import { selectors as cartSelectors } from '@elab/core/store/cart';
import { selectors as configSelectors } from '@elab/core/store/config';
import {
    actions as globalActions,
    selectors as globalSelectors,
    types as globalTypes,
} from '@elab/core/store/global';
import { createWatcher } from '@elab/core/store/core/saga';
import { actions as uiActions, types as uiTypes } from '@elab/core/store/ui';
import logger from '@elab/core/utils/logger';
import { getRegisteredOnCheckout } from '@elab/core/utils/authCookiesManager';
import {
    defineAdditionalAddressFieldsDisplay,
    getConfirmationFromModal,
    initAddressSuggestionWatchers,
} from '@elab/core/utils/address/sagaHelpers';
import { ageVerified } from '@elab/core/utils/validations';
import { updateConstraint } from '@elab/core/utils/constraints/sagaHelpers';
import { getPrefillValue } from '@elab/core/utils/personalInfo';

import customDtoBuilder from './customDtoBuilder';
import getActions from './actions';
import getSelectors from './selectors';
import getTypes from './types';

export function* getIsRegisteredOnCheckout() {
    const shouldSkipVerificationModal = yield call(getRegisteredOnCheckout);
    if (shouldSkipVerificationModal) return shouldSkipVerificationModal;

    return yield select(userSelectors.isRegisteredOnCheckout);
}

export function* checkAddress() {
    const { types, actions, selectors } = yield getContext('ctx');
    yield take(types.AFTER_INITIALIZE);

    const addressValidationMessage = yield select(
        cartSelectors.getMessagesBySource,
        'ADDRESS_CONSISTENCY_VALIDATION',
    );
    const deliveryAddressValidation = yield select(
        cartSelectors.getMessagesBySource,
        'DELIVERY_ADDRESS_VALIDATION',
    );

    const skipVerificationModal = yield call(getIsRegisteredOnCheckout);

    if (
        !skipVerificationModal &&
        addressValidationMessage &&
        Object.entries(addressValidationMessage).length &&
        !(deliveryAddressValidation && Object.entries(deliveryAddressValidation).length)
    ) {
        yield put(actions.setAddressValidation(false));

        const { immediatelyDialogOnCheckoutPersonalForm } = yield select(
            configSelectors.getAddressVerificationDialogConfig,
        );

        if (immediatelyDialogOnCheckoutPersonalForm) {
            yield fork(getConfirmationFromModal, { types, actions, selectors });
        }
    }
}

export function* mergeWithUserProfile(profileDto) {
    const userProfile = yield select(userSelectors.getProfile);
    const formValues = yield call(customDtoBuilder, profileDto, {});
    const modifiedProfile = yield call(_merge, userProfile, formValues);
    const clearSameValue = !(yield select(
        configSelectors.titleGenderMappingDisabledPersonalInformationForm,
    ));
    if (clearSameValue && userProfile.titleCode === modifiedProfile.titleCode) {
        modifiedProfile.titleCode = '';
    }
    return modifiedProfile;
}

export function* doSubmit(profileDto) {
    const { actions, selectors } = yield getContext('ctx');

    yield put(globalActions.resetFormsMessages());
    const preventRedirects = yield select(configSelectors.preventRedirectPersonalInformationForm);
    const submitUrl = yield select(selectors.getSubmitUrl);
    const submitMethod = yield select(selectors.getSubmitMethod);
    let userDataToUpdate = {};
    if (submitMethod === 'PUT') {
        userDataToUpdate = yield call(mergeWithUserProfile, profileDto);
    } else {
        userDataToUpdate = yield call(dynaForms.dtoBuilder, profileDto, {});
    }
    const shouldCleanPhoneNumber = yield select(configSelectors.cleanPhoneNumber);
    if (shouldCleanPhoneNumber) {
        userDataToUpdate = cleanPhoneNumber(userDataToUpdate);
    }
    yield call(dynaFormsApi.doSubmit, submitUrl, userDataToUpdate, submitMethod);
    yield put(userActions.loadProfile({ skipCustomerStatusDelay: true }));

    if (preventRedirects) {
        yield put(actions.setGlobalSuccess());
        return;
    }

    const successRedirectUrl = yield select(selectors.getSuccessRedirectLink);
    if (successRedirectUrl) {
        yield take(userTypes.SET_PROFILE);
        yield put(globalActions.redirect(successRedirectUrl));
    }
}

export function* prefillFormFields(storeName) {
    const { actions, selectors } = yield getContext('ctx');

    let data = yield select(selectors.getData);
    if (!data) {
        yield take(userTypes.SET_PROFILE);
        data = yield select(selectors.getData);
    }
    const fieldNames = Object.keys(data);
    const setMobileNumber = yield select(configSelectors.setMobileNumber);
    const mobileNumberData = yield select(selectors.getMobileNumber);
    if (!fieldNames.length) return;

    const user = yield select(userSelectors.getProfile);
    yield put(actions.setShouldPrefill(false));

    yield call(defineAdditionalAddressFieldsDisplay, {
        storeName,
        valuesSource: user,
        extractValuesWith: getPrefillValue,
    });
    const preventPrefillFieldsByForm = yield select(
        configSelectors.getPreventPrefillFieldsByForm,
        storeName,
    );

    const skipPhonePrefill = yield select(selectors.getShouldPhonePrefill);
    yield all(
        fieldNames
            .filter(fieldName => {
                if (skipPhonePrefill) {
                    return !preventPrefillFieldsByForm.includes(fieldName);
                }
                return true;
            })
            .map(fieldName =>
                put(actions.initDataFieldValue(fieldName, getPrefillValue(user, fieldName))),
            ),
    );
    if (setMobileNumber && fieldNames.includes('mobileNumber')) {
        const { defaultAddress = {} } = user;
        let mobileNumber = defaultAddress.formattedPhone || defaultAddress.mobileNumber || null;
        if (skipPhonePrefill && !mobileNumber) {
            mobileNumber = mobileNumberData.defaultValue;
        }

        yield put(actions.initDataFieldValue('mobileNumber', mobileNumber));
    }
}

export function* needConsent() {
    const { selectors } = yield getContext('ctx');
    const dateOfBirth = yield select(selectors.getAgeVerification);
    const setDateOfBirth = yield select(selectors.getDataFieldValue, 'dateOfBirth');
    const isValidAge = yield call(ageVerified, setDateOfBirth, null, dateOfBirth.parameters);

    return dateOfBirth && !isValidAge;
}

export function* afterStepValidation() {
    const { types, actions, selectors } = yield getContext('ctx');

    const metadata = yield select(selectors.getMetadata);
    const skipVerificationModal = yield call(getRegisteredOnCheckout);
    let canContinue = true;
    try {
        if (
            !skipVerificationModal &&
            !(yield getConfirmationFromModal({ types, actions, selectors }))
        ) {
            return false;
        }

        const useAgeVerification = yield select(configSelectors.useAgeVerification);
        if (useAgeVerification && metadata[0].fields.dateOfBirth) {
            const consentNeeded = yield call(needConsent);
            if (consentNeeded) {
                const dateOfBirthValue = yield select(selectors.getDataFieldValue, 'dateOfBirth');
                const savedDateOfBirthValue = yield select(selectors.getSavedDateOfBirthValue);
                if (dateOfBirthValue != savedDateOfBirthValue) {
                    yield put(uiActions.showModal(types.CONSENT_NEEDED_DIALOG));
                    const modalAction = yield take([uiTypes.OK_MODAL, uiTypes.CANCEL_MODAL]);
                    if (modalAction.type !== uiTypes.OK_MODAL) {
                        canContinue = false;
                    }
                }
            }
        }
    } catch (e) {
        yield call(logger.error, e);
    }
    return canContinue;
}
export function* handleAfterInitialize(storeName) {
    const { selectors } = yield getContext('ctx');
    yield call(updateConstraintsByInitialize);

    const shouldPrefill = yield select(selectors.getShouldPrefill);

    if (shouldPrefill) {
        yield prefillFormFields(storeName);
    }
}

function* updateConstraintsByInitialize() {
    const isLoyaltyUser = yield select(userSelectors.isLoyalty);
    if (!isLoyaltyUser) {
        return;
    }
    const country = yield select(globalSelectors.getCountry);
    const shouldValidateByAge = yield select(configSelectors.shouldValidateByAge, country);
    if (!shouldValidateByAge) {
        return;
    }

    yield call(updateConstraint, 'dateOfBirth', 'ageVerification');
}

export default storeName => (formId, metadata) => {
    const actions = getActions(storeName);
    const selectors = getSelectors(storeName);
    const types = getTypes(storeName);
    const ctx = { actions, selectors, types, storeName };

    return function* PersonalInformationFormSaga() {
        yield all([
            ...dynaForms.createSagaFor(
                ctx,
                metadata || dynaFormsApi.getFormMetadata(formId || storeName),
                doSubmit,
                afterStepValidation,
                false,
            ),
            createWatcher(globalTypes.RESET_FORMS_MESSAGES, actions.resetGlobalMessage()),
            takeEvery(types.AFTER_INITIALIZE, handleAfterInitialize, storeName),
            takeEvery(userTypes.SET_PROFILE, prefillFormFields, storeName),
            takeEvery(types.DO_ADDRESS_VERIFICATION, checkAddress),
            initAddressSuggestionWatchers(ctx),
            initAddressRelationsWatchers(ctx, storeName),
        ]);
    };
};
